import { useState } from 'react'
import { isAxiosError } from 'axios'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

type ExportXlsxButtonProps = {
  download: () => Promise<void>
  hasActiveFilters: boolean
}

export function ExportXlsxButton({
  download,
  hasActiveFilters,
}: ExportXlsxButtonProps) {
  const { t } = useTranslation('packagingSuppliers')
  const [isExporting, setIsExporting] = useState(false)
  const [isFiltersDialogOpen, setIsFiltersDialogOpen] = useState(false)

  const runExport = async () => {
    setIsExporting(true)
    try {
      await download()
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 413) {
        toast.error(t('export.tooLarge'))
        return
      }
      toast.error(t('export.failed'))
    } finally {
      setIsExporting(false)
    }
  }

  const handleExportClick = () => {
    if (hasActiveFilters) {
      setIsFiltersDialogOpen(true)
      return
    }
    void runExport()
  }

  const handleProceedExport = () => {
    setIsFiltersDialogOpen(false)
    void runExport()
  }

  const handleCancelExport = () => {
    setIsFiltersDialogOpen(false)
  }

  let exportButtonLabel = t('export.button')
  if (isExporting) {
    exportButtonLabel = t('export.exporting')
  }

  return (
    <>
      <Button
        type='button'
        variant='outline'
        size='sm'
        className='h-8 shrink-0'
        disabled={isExporting}
        onClick={handleExportClick}
      >
        {isExporting && (
          <Loader2 className='size-4 animate-spin' aria-hidden />
        )}
        {exportButtonLabel}
      </Button>

      <Dialog open={isFiltersDialogOpen} onOpenChange={setIsFiltersDialogOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>{t('export.filtersDialog.title')}</DialogTitle>
            <DialogDescription>{t('export.filtersDialog.body')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={handleCancelExport}
            >
              {t('export.filtersDialog.cancel')}
            </Button>
            <Button type='button' onClick={handleProceedExport}>
              {t('export.filtersDialog.proceed')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
